import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { RON } from "@/lib/api";

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, remove, total } = useCart();

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-50 bg-ink/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
          />
          <motion.aside
            className="fixed top-0 right-0 z-50 h-full w-full max-w-md bg-cream flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 h-20 border-b border-line">
              <h2 className="font-display text-3xl">Coșul tău</h2>
              <button onClick={() => setOpen(false)} data-testid="cart-close" aria-label="Închide">
                <X size={22} />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center px-6">
                <ShoppingBag size={40} className="text-sand" />
                <p className="text-muted-foreground">Coșul este gol.</p>
                <Link
                  to="/produse"
                  onClick={() => setOpen(false)}
                  className="overline text-roast border-b border-roast pb-1"
                  data-testid="cart-empty-shop"
                >
                  Descoperă cafeaua
                </Link>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
                  {items.map((i) => (
                    <div key={i.key} className="flex gap-4" data-testid={`cart-item-${i.product_id}`}>
                      <img src={i.image} alt={i.name} className="h-24 w-20 object-cover bg-stone" />
                      <div className="flex-1">
                        <p className="overline text-roast">{i.brand}</p>
                        <p className="font-display text-xl leading-tight">{i.name}</p>
                        <p className="text-xs text-muted-foreground">{i.grammage}</p>
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center border border-line">
                            <button onClick={() => updateQty(i.key, i.quantity - 1)} className="p-1.5" aria-label="minus">
                              <Minus size={13} />
                            </button>
                            <span className="w-8 text-center text-sm">{i.quantity}</span>
                            <button onClick={() => updateQty(i.key, i.quantity + 1)} className="p-1.5" aria-label="plus">
                              <Plus size={13} />
                            </button>
                          </div>
                          <span className="font-medium">{RON(i.price * i.quantity)}</span>
                        </div>
                      </div>
                      <button onClick={() => remove(i.key)} className="text-muted-foreground hover:text-roast" aria-label="Șterge">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="border-t border-line px-6 py-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="overline">Subtotal</span>
                    <span className="font-display text-2xl">{RON(total)}</span>
                  </div>
                  <Link
                    to="/finalizare"
                    onClick={() => setOpen(false)}
                    data-testid="cart-checkout-btn"
                    className="block text-center bg-ink text-cream py-4 hover:bg-roast transition-colors duration-300 text-sm font-medium tracking-wide"
                  >
                    Finalizează comanda
                  </Link>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
